import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { IndicadoresLista } from "../../domain/models/indicadores_lista";
import { QualidadeIndicadoresForm } from "../schemas/Indicadores/qualidade_indicadores";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useIndicadoresFilters } from "../hooks/Qualidade_indicadores/filter";
import { useIndicadoresPagination } from "../hooks/Qualidade_indicadores/pagination";

import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

type LoadIndicadoresListaListaFuncState = {
  IdIndicador?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  status?: number,
  pesquisa?: string | number | Date,
};

export const useQualidadeIndicadoresListaViewModel = () => {
  const {
    commons: {getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    reports: { getReportIndicadoresUseCase},
    qualidade: {getAllListagemIndicadoresUseCase, deleteIndicadoresUseCase, createIndicadoresUseCase, updateIndicadoresUseCase }
   
  } = useDependencyInjection();
  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });
  
  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [indicadoresModal, setIndicadoresModal] = useState<boolean>(false);
  const [indicadoresLista, setindicadoresLista] = useState<IndicadoresLista[]>([])
  const [selectedIdIndicadores, setSelectedIdIndicadores] = useState<IndicadoresLista>();

  //Arquivo
  const [indicadoresaDeleteModal, setIndicadoresaDeleteModal] = useState<boolean>(false);

  const {pushErrorNotification, pushInfoNotification } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useIndicadoresFilters();
  const pagination = useIndicadoresPagination();
  

  // #region Funções de carregamento

  const loadIndicadoresListaState = useCallback(async (funcState?: LoadIndicadoresListaListaFuncState) => {
      try {
        const filterID_Indicador = evaluateParamValue(funcState?.IdIndicador, filters.IdIndicador);
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
        const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
        const filteredStatus = funcState?.status ?? filters.status;
        await getAllListagemIndicadoresUseCase.getAllListagemIndicadores(
          {
            IdIndicador: filterID_Indicador,  PesquisarPor: filteredTipoPesquisa, Pesquisar: String(filteredPesquisa ), status: filteredStatus, 
          },
          filteredPage,
          filteredRowsPerPage
        );
        setindicadoresLista(getAllListagemIndicadoresUseCase.items);
        pagination.setRowsPerPage(getAllListagemIndicadoresUseCase.pageSize);
        pagination.setTotalItems(getAllListagemIndicadoresUseCase.totalItems);
      } catch (e: unknown) {
          throw new Error("Falha ao Indicadores Lista!");
      }
    }, [filters.IdIndicador, filters.pesquisa, filters.status, filters.tipoPesquisa, getAllListagemIndicadoresUseCase, pagination]);


  //#endregion

  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      jobs.push(loadIndicadoresListaState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadIndicadoresListaState());
      
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadIndicadoresListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos

  const handleIndicadoresSubmit = useCallback(async (form: QualidadeIndicadoresForm) => {
      try {
        let Ativo = form.Ativo === 1 ? true : false
        if(selectedIdIndicadores){
          await updateIndicadoresUseCase.updateIndicadores(selectedIdIndicadores.IdIndicador, form.Item ?? null, form.Descricao ?? null, form.Indice ?? null, form.Frequencia ?? null, form.Meta ?? null, Ativo ?? null, form.Resultado ?? null);
          pushInfoNotification("Sucesso", "Indicador atualizado!", { isAutoFinish: true });
          setIndicadoresModal(false);
        }else{
          await createIndicadoresUseCase.createIndicadores(form.Item ?? null, form.Descricao ?? null, form.Indice ?? null, form.Frequencia ?? null, form.Meta ?? null, Ativo ?? null, form.Resultado ?? null);
          pushInfoNotification("Sucesso", "Indicador criado!", { isAutoFinish: true });
          setIndicadoresModal(false);
        }
        loadIndicadoresListaState();
      } catch (e) {
          pushErrorNotification("Erro", processError(e, 'Falha ao criar Indicador!'));
      }finally {
        setSaving(false);
      }
    },[createIndicadoresUseCase, loadIndicadoresListaState, pushErrorNotification, pushInfoNotification, selectedIdIndicadores, updateIndicadoresUseCase]);

  const handleCadastroIndicadoresOpenState = useCallback((IdIndicador: number | null, openState: boolean) => {
      if (openState) {
        if (IdIndicador) {
          setSelectedIdIndicadores(indicadoresLista.find(x => x.IdIndicador === IdIndicador));
          loadIndicadoresListaState({IdIndicador: IdIndicador});
        } else {
          setSelectedIdIndicadores(undefined);
        }
      }
      setIndicadoresModal(openState);
    }, [indicadoresLista, loadIndicadoresListaState]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadIndicadoresListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadIndicadoresListaState, pagination, pushErrorNotification]);

  
  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
  
        await loadIndicadoresListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadIndicadoresListaState, pagination, pushErrorNotification]);

   const handleDeleteIndicadoresModalOpenState = useCallback((IdIndicador: number | null, openState: boolean) => {
      if (openState) {
        if (IdIndicador && indicadoresLista) {
          setSelectedIdIndicadores(indicadoresLista.find(x => x.IdIndicador === IdIndicador));
        } else {
          setSelectedIdIndicadores(undefined);
        }
      }
      setIndicadoresaDeleteModal(openState);
    }, [indicadoresLista]);
  

  const handleDeleteConfirmaIndicadores = useCallback(async () => {
    if (selectedIdIndicadores) {        try {
          setSaving(true);
          await deleteIndicadoresUseCase.deleteIndicadores(selectedIdIndicadores.IdIndicador);
          await loadIndicadoresListaState();
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
        } finally {
          setSaving(false);
          setIndicadoresaDeleteModal(false);
        }
      }
    }, [deleteIndicadoresUseCase, loadIndicadoresListaState, pushErrorNotification, selectedIdIndicadores]);

  

  //#endregion



  //#region Filtros
  const handleClearFilters = useCallback(async () => {
      try {
          setLoading(true);
          filters.clearFilters();
          pagination.setPage(1);
          await loadIndicadoresListaState();
      } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao buscar Indicador!'));
      } finally {
          setLoading(false);
      }
  }, [filters, pagination, loadIndicadoresListaState, pushErrorNotification]);

  const handlePesquisaChange = useDebouncedCallback( debouncedRef,  (newValue: string | Date | number | null) => {
      useIndicadoresFilters.getState().setPesquisa(newValue);
      
      if (newValue) {
        return async () => {
          try {
            setLoading(true);
            useIndicadoresPagination.getState().setPage(1);
            await loadIndicadoresListaState({ pesquisa: newValue, page: 1 });
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar Indicador!"));
          } finally {
            setLoading(false);
          }
        }
      } else {
        return async () => {
          try {
            setLoading(true);
            useIndicadoresPagination.getState().setPage(1);
            await loadIndicadoresListaState(); 
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar Indicador!"));
          } finally {
            setLoading(false);
          }
        }
      }
    }, 
    [ loadIndicadoresListaState, pushErrorNotification, setLoading]
  );
  
  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useIndicadoresFilters.getState().setTipoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useIndicadoresPagination.getState().setPage(1);
        await loadIndicadoresListaState({ tipoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Indicador!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadIndicadoresListaState, pushErrorNotification]);

  const handleStatusChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
      useIndicadoresFilters.getState().setStatus(newValue);
      return async () => {
      try {
        setLoading(true);
        useIndicadoresPagination.getState().setPage(1);
        await loadIndicadoresListaState({ status: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Indicador!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadIndicadoresListaState, pushErrorNotification]);

  //#endregion
  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      }
    },
    [changeHistory],
  );
  
  //#region Relatorio
  const handleSubmitReport = useCallback(async () => {
      try {
        setLoading(true);
  
        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportIndicadoresUseCase.getReportIndicadores(
              requiredMimeType,
            ),
          closeReportModalCallback: () => {
            // setOpenReportModal(false);
          }
        });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio"));
      } finally {
        setLoading(false);
      }
    }, [getReportIndicadoresUseCase, pushErrorNotification, startReportViewerProcess]);

  //#endregion

  return {
      loading,
      saving,
      errorMessage,
      pagination,
      filters,
      indicadoresLista,
      selectedIdIndicadores,
      indicadoresModal,
      indicadoresaDeleteModal,
      changeHistory,
      reportViewerState,
      handleOpenChangeHistory,
      handleDeleteIndicadoresModalOpenState,
      handleReload,
      handleRowsPerPageChange,
      handlePageChange,
      handleTipoPesquisaChange,
      handlePesquisaChange,
      handleClearFilters,
      handleStatusChange,
      handleIndicadoresSubmit,
      handleDeleteConfirmaIndicadores,
      handleCadastroIndicadoresOpenState,
      handleSubmitReport
  }
}